import axios from 'axios';

// API 기본 URL 설정
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://pivo-api.fly.dev';

// Axios 인스턴스 생성
export const $api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 30초 타임아웃 (파일 업로드용)
  headers: {
    'Content-Type': 'application/json',
  },
});

// 요청 인터셉터 - JWT 토큰 자동 추가
$api.interceptors.request.use(
  (config) => {
    // localStorage에서 토큰 가져오기
    const authStorage = localStorage.getItem('auth-storage');
    if (authStorage) {
      try {
        const { state } = JSON.parse(authStorage);
        if (state && state.token) {
          config.headers.Authorization = `Bearer ${state.token}`;
        }
      } catch (error) {
        console.warn('[AXIOS] Token parsing error:', error);
      }
    }
    
    // 요청 로깅 (개발 모드에서만)
    if (process.env.REACT_APP_ENV === 'development') {
      console.log('[API_REQUEST]', config.method?.toUpperCase(), config.url, config.params);
    }
    return config;
  },
  (error) => {
    console.error('[API_REQUEST_ERROR]', error);
    return Promise.reject(error);
  }
);

// 응답 인터셉터 - 에러 처리
$api.interceptors.response.use(
  (response) => {
    if (process.env.REACT_APP_ENV === 'development') {
      console.log('[API_RESPONSE]', response.status, response.config.url);
    }
    return response;
  },
  (error) => {
    console.error('[API_RESPONSE_ERROR]', error.response?.status, error.response?.data?.message || error.message);
    
    // 401 에러시 토큰 제거 및 로그인 페이지로 리다이렉트
    if (error.response?.status === 401) {
      localStorage.removeItem('auth-storage');
      // 현재 경로가 로그인/회원가입이 아닐 때만 리다이렉트
      if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// API 응답 표준화 함수
const createApiResponse = (response) => {
  console.log('[DEBUG] createApiResponse 원시 응답:', response.data);
  
  const responseData = response.data;
  
  // 서버가 { success: true, message: "...", data: {...} } 구조로 응답
  if (responseData && typeof responseData === 'object') {
    return {
      success: responseData.success !== false, // success가 false가 아니면 true로 간주
      data: responseData.data || responseData, // data가 있으면 data, 없으면 전체 응답
      message: responseData.message || 'Success',
      timestamp: responseData.timestamp || new Date().toISOString()
    };
  }
  
  // 예상치 못한 응답 구조
  console.warn('[API] 예상치 못한 응답 구조:', responseData);
  return {
    success: true,
    data: responseData,
    message: 'Success',
    timestamp: new Date().toISOString()
  };
};

const createApiError = (error) => {
  console.log('[DEBUG] createApiError 입력:', error);
  
  return {
    success: false,
    message: error.response?.data?.message || error.message || '알 수 없는 오류가 발생했습니다.',
    error: error.response?.data,
    status: error.response?.status
  };
};
// 인증 API
export const authAPI = {
  // 로그인
  login: async (email, password) => {
    try {
      const response = await $api.post('/api/auth/login', {
        email,
        password
      });
      return createApiResponse(response);
    } catch (error) {
      return createApiError(error);
    }
  },

  // 회원가입
  register: async (userData) => {
    try {
      const response = await $api.post('/api/auth/register', {
        username: userData.username,
        email: userData.email,
        password: userData.password,
        registration_code: userData.registration_code  // 🆕 인증코드 포함
      });
      return createApiResponse(response);
    } catch (error) {
      return createApiError(error);
    }
  }
};

// 파일 관리 API
export const fileAPI = {
  // 파일 목록 조회 - 백엔드 API 연동
  getFileList: async (options = {}) => {
    try {
      const {
        status = 'active',     // 'active', 'deleted', 'all'
        page = 1,             // 페이지 번호
        limit = 20,           // 페이지당 파일 수
        sort_by = 'uploaded_at', // 정렬 기준
        sort_order = 'desc'   // 정렬 순서
      } = options;

      const params = {
        status,
        page: Math.max(1, parseInt(page)),
        limit: Math.max(1, Math.min(100, parseInt(limit))), // 최대 100개 제한
        sort_by,
        sort_order
      };

      console.log('[FILE_API]', 'Getting file list with params:', params);

      const response = await $api.get('/api/files', { params });
      
      const result = createApiResponse(response);
      console.log('[FILE_API]', 'File list retrieved:', {
        totalFiles: result.data.pagination?.total_files || 0,
        currentPage: result.data.pagination?.current_page || 1,
        totalPages: result.data.pagination?.total_pages || 0
      });
      
      return result;
    } catch (error) {
      console.error('[FILE_API]', 'Get file list error:', error);
      return createApiError(error);
    }
  },

  // 파일 검색 (파일명으로 필터링)
  searchFiles: async (searchTerm, options = {}) => {
    try {
      const searchOptions = {
        ...options,
        search: searchTerm.trim()
      };

      // 백엔드에 검색 파라미터 추가 구현 필요
      const response = await $api.get('/api/files', { 
        params: searchOptions 
      });
      
      return createApiResponse(response);
    } catch (error) {
      return createApiError(error);
    }
  },

  // 파일 업로드
  upload: async (file, dataname ,description = '') => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('dataname', dataname);
      formData.append('description', description);

      const response = await $api.post('/api/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // 파일 업로드는 1분 타임아웃
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log('[FILE_UPLOAD_PROGRESS]', `${percentCompleted}%`);
        }
      });
      
      return createApiResponse(response);
    } catch (error) {
      return createApiError(error);
    }
  },

  // 파일 재업로드
  reupload: async (fileId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await $api.put(`/api/files/${fileId}/reupload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log('[FILE_REUPLOAD_PROGRESS]', `${percentCompleted}%`);
        }
      });
      
      return createApiResponse(response);
    } catch (error) {
      return createApiError(error);
    }
  },

  // 파일 삭제
  delete: async (fileId) => {
    try {
      const response = await $api.delete(`/api/files/${fileId}`);
      return {
        success: true,
        message: response.data.message,
        timestamp: response.data.timestamp
      };
    } catch (error) {
      return createApiError(error);
    }
  },

  // 파일 복사
  copy: async (fileId, newFilename = '') => {
    try {
      const response = await $api.post(`/api/files/${fileId}/copy`, {
        new_filename: newFilename
      });
      return createApiResponse(response);
    } catch (error) {
      return createApiError(error);
    }
  },

  // 파일 상세 정보 조회
  getFileInfo: async (fileId) => {
    try {
      const response = await $api.get(`/api/files/${fileId}`);
      return createApiResponse(response);
    } catch (error) {
      return createApiError(error);
    }
  },

  // 파일 다운로드 URL 생성
  getDownloadUrl: (fileId) => {
    const token = localStorage.getItem('auth-storage');
    let tokenString = '';
    
    if (token) {
      try {
        const { state } = JSON.parse(token);
        tokenString = state.token;
      } catch (error) {
        console.warn('[FILE_API] Token parsing error for download URL:', error);
      }
    }
    
    return `${API_BASE_URL}/api/files/${fileId}/download?token=${tokenString}`;
  }
};

// 데이터 매핑 API
export const mappingAPI = {
  // 변수 매핑 실행
  mapVariables: async (fileId, mappingData) => {
    try {
      const response = await $api.post(`/api/mapping/${fileId}`, mappingData);
      return createApiResponse(response);
    } catch (error) {
      return createApiError(error);
    }
  },

  // 매핑 결과 조회
  getMappingResult: async (fileId) => {
    try {
      const response = await $api.get(`/api/mapping/${fileId}`);
      return createApiResponse(response);
    } catch (error) {
      return createApiError(error);
    }
  },

  // 매핑 초기화
  resetMapping: async (fileId) => {
    try {
      const response = await $api.delete(`/api/mapping/${fileId}`);
      return {
        success: true,
        message: response.data.message,
        timestamp: response.data.timestamp
      };
    } catch (error) {
      return createApiError(error);
    }
  }
};


// 데이터 트리밍 API
export const trimAPI = {
  // 파일 구조 분석
  analyzeFile: async (fileId, headerRows = 6) => {
    try {
      const response = await $api.post(`/api/trim/${fileId}/analyze`, {
        header_rows: headerRows
      });
      return createApiResponse(response);
    } catch (error) {
      return createApiError(error);
    }
  },

  // 선택된 컬럼 미리보기
  generatePreview: async (fileId, selectedColumns, headerRows = 6, previewRows = 10) => {
    try {
      const response = await $api.post(`/api/trim/${fileId}/preview`, {
        selected_columns: selectedColumns,
        header_rows: headerRows,
        preview_rows: previewRows
      });
      return createApiResponse(response);
    } catch (error) {
      return createApiError(error);
    }
  },

  // 트림 실행
  executeTrim: async (fileId, selectedColumns, outputFilename, headerRows = 6) => {
    try {
      const response = await $api.post(`/api/trim/${fileId}/execute`, {
        selected_columns: selectedColumns,
        output_filename: outputFilename,
        header_rows: headerRows
      });
      return createApiResponse(response);
    } catch (error) {
      return createApiError(error);
    }
  },
  // 🔥 추가: 트림된 파일 다운로드
  downloadTrimmedFile: async (fileId, filename) => {
    try {
      const response = await $api.get(`/api/trim/${fileId}/download/${filename}`, {
        responseType: 'blob' // 파일 다운로드를 위한 blob 타입
      });
      
      // 파일 다운로드 처리
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      return {
        success: true,
        message: '파일이 성공적으로 다운로드되었습니다.'
      };
    } catch (error) {
      return createApiError(error);
    }
  },

  // 🔥 추가: 다운로드 URL 생성 (직접 다운로드용)
  getDownloadUrl: (fileId, filename) => {
    const token = localStorage.getItem('auth-storage');
    let tokenString = '';
    
    if (token) {
      try {
        const { state } = JSON.parse(token);
        tokenString = state.token;
      } catch (error) {
        console.warn('[TRIM_API] Token parsing error for download URL:', error);
      }
    }
    
    return `${API_BASE_URL}/api/trim/${fileId}/download/${filename}?token=${tokenString}`;
  }
};

// Input Data 관리 API
export const inputDataAPI = {
  // Input Data 업로드
  upload: async (file, name, description = '') => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', name);
      formData.append('description', description);

      const response = await $api.post('/api/input-data/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 240000, // 2분 타임아웃 (JSON 변환 처리 시간 고려)
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log('[INPUT_DATA_UPLOAD_PROGRESS]', `${percentCompleted}%`);
        }
      });
      
      return createApiResponse(response);
    } catch (error) {
      return createApiError(error);
    }
  },

  // Input Data 목록 조회
  getList: async () => {
    try {
      const response = await $api.get('/api/input-data');
      return createApiResponse(response);
    } catch (error) {
      return createApiError(error);
    }
  },

  // 특정 Input Data 상세 조회
  getDetail: async (inputDataId) => {
    try {
      const response = await $api.get(`/api/input-data/${inputDataId}`);
      return createApiResponse(response);
    } catch (error) {
      return createApiError(error);
    }
  },

  // Input Data 삭제
  delete: async (inputDataId) => {
    try {
      const response = await $api.delete(`/api/input-data/${inputDataId}`);
      return {
        success: true,
        message: response.data.message,
        timestamp: response.data.timestamp
      };
    } catch (error) {
      return createApiError(error);
    }
  }
};

// 기본 보고서 서식 API
export const defaultTemplateAPI = {
  getStatus: async () => {
    try {
      const response = await $api.get('/api/default-templates/status');
      return createApiResponse(response);
    } catch (error) {
      return createApiError(error);
    }
  },

  update: async (templateNames = []) => {
    try {
      const payload = templateNames.length > 0 ? { template_names: templateNames } : {};
      const response = await $api.post('/api/default-templates/update', payload, {
        timeout: 60000
      });
      return createApiResponse(response);
    } catch (error) {
      return createApiError(error);
    }
  },

  getUpdateJob: async (jobId) => {
    try {
      const response = await $api.get(`/api/default-templates/update-jobs/${jobId}`);
      return createApiResponse(response);
    } catch (error) {
      return createApiError(error);
    }
  }
};

// 보고서 생성 API  
export const reportsAPI = {
  // 보고서 생성 (기존 함수 수정)
  generate: async (reportData) => {
    try {
      const response = await $api.post('/api/reports/generate', reportData, {
        timeout: 600000 // 5분 타임아웃 (보고서 생성 시간 고려)
      });
      return createApiResponse(response);
    } catch (error) {
      return createApiError(error);
    }
  },

  // 보고서 생성 작업 시작
  createGenerateJob: async (reportData) => {
    try {
      const response = await $api.post('/api/reports/generate-jobs', reportData, {
        timeout: 60000
      });
      return createApiResponse(response);
    } catch (error) {
      return createApiError(error);
    }
  },

  // 보고서 생성 작업 상태 조회
  getGenerateJob: async (jobId) => {
    try {
      const response = await $api.get(`/api/reports/generate-jobs/${jobId}`);
      return createApiResponse(response);
    } catch (error) {
      return createApiError(error);
    }
  },

  // 보고서 목록 조회 (기존 함수 유지)
  getReportList: async () => {
    try {
      const response = await $api.get('/api/reports');
      return createApiResponse(response);
    } catch (error) {
      return createApiError(error);
    }
  },

  // 보고서 다운로드 URL 생성 (수정)
  getReportDownloadUrl: (filename) => {
    const token = localStorage.getItem('auth-storage');
    let tokenString = '';
    
    if (token) {
      try {
        const { state } = JSON.parse(token);
        tokenString = state.token;
      } catch (error) {
        console.warn('[REPORTS_API] Token parsing error for download URL:', error);
      }
    }
    
    return `${API_BASE_URL}/api/reports/download/${filename}?token=${tokenString}`;
  },

  // 🆕 보고서 직접 다운로드 (Blob 처리)
  // downloadReport: async (filename) => {
  //   try {
  //     const response = await $api.get(`/api/reports/download/${filename}`, {
  //       responseType: 'blob'
  //     });
      
  //     // 파일 다운로드 처리
  //     const blob = new Blob([response.data], { 
  //       type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  //     });
      
  //     const downloadUrl = window.URL.createObjectURL(blob);
  //     const link = document.createElement('a');
  //     link.href = downloadUrl;
  //     link.download = filename;
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //     window.URL.revokeObjectURL(downloadUrl);
      
  //     return {
  //       success: true,
  //       message: '보고서가 성공적으로 다운로드되었습니다.'
  //     };
  //   } catch (error) {
  //     return createApiError(error);
  //   }
  // }
  downloadReport: async (filename) => {
    try {
      console.log('[REPORTS_API_DEBUG] Starting download for filename:', filename);
      
      const response = await $api.get(`/api/reports/download/${filename}`, {
        responseType: 'blob'
      });
      
      console.log('[REPORTS_API_DEBUG] Download response:', {
        status: response.status,
        headers: response.headers,
        dataType: typeof response.data,
        dataSize: response.data.size
      });
      
      // 🔧 수정: 응답이 JSON 에러인지 확인
      if (response.data.type === 'application/json') {
        // Blob이 JSON인 경우 에러 응답으로 처리
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        console.error('[REPORTS_API_DEBUG] Server returned JSON error:', errorData);
        
        return {
          success: false,
          message: errorData.message || '다운로드 중 오류가 발생했습니다.'
        };
      }
      
      // 🔧 수정: 파일 크기 확인
      if (response.data.size === 0) {
        console.error('[REPORTS_API_DEBUG] Empty file received');
        return {
          success: false,
          message: '빈 파일이 다운로드되었습니다.'
        };
      }
      
      // 🔧 수정: 사용자 ID 제거하여 실제 파일명 추출
      let cleanFilename = filename;
      
      // 파일명에서 user_id 패턴 제거 (UUID_filename 형태)
      const userIdPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}_/;
      if (userIdPattern.test(filename)) {
        cleanFilename = filename.replace(userIdPattern, '');
        console.log('[REPORTS_API_DEBUG] Cleaned filename:', cleanFilename);
      }
      
      // 파일 다운로드 처리
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = cleanFilename;  // 🔧 수정: 정리된 파일명 사용
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      console.log('[REPORTS_API_DEBUG] Download completed successfully with filename:', cleanFilename);
      
      return {
        success: true,
        message: '보고서가 성공적으로 다운로드되었습니다.'
      };
    } catch (error) {
      console.error('[REPORTS_API_DEBUG] Download error:', error);
      
      // 🔧 추가: 에러 상세 정보 출력
      if (error.response) {
        console.error('[REPORTS_API_DEBUG] Error response status:', error.response.status);
        console.error('[REPORTS_API_DEBUG] Error response headers:', error.response.headers);
        
        // Blob 응답인 경우 텍스트로 변환하여 에러 메시지 확인
        if (error.response.data instanceof Blob) {
          try {
            const errorText = await error.response.data.text();
            console.error('[REPORTS_API_DEBUG] Error response body:', errorText);
            
            // JSON 파싱 시도
            const errorData = JSON.parse(errorText);
            return createApiError({ ...error, message: errorData.message });
          } catch (parseError) {
            console.error('[REPORTS_API_DEBUG] Failed to parse error response:', parseError);
          }
        }
      }
      
      return createApiError(error);
    }
  }
};

// 서버 상태 확인
export const systemAPI = {
  healthCheck: async () => {
    try {
      const response = await $api.get('/api/health');
      return {
        success: true,
        message: response.data.message,
        timestamp: response.data.timestamp
      };
    } catch (error) {
      return createApiError(error);
    }
  },

  // 서버 통계 정보
  getStats: async () => {
    try {
      const response = await $api.get('/api/stats');
      return createApiResponse(response);
    } catch (error) {
      return createApiError(error);
    }
  }
};

export default $api;